import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/elements/Button";
import { IconBox } from "@/components/elements/IconBox";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { IconClose, IconSave, IconWarningInfo } from "@/assets/icons";
import { AppRoutes } from "@/routes/config";
import { background, black, divider, orange, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { BulkImportLearnerTable } from "@/features/learner/components/learner-bulk-import/BulkImportLearnerTable";
import { BulkChangeModal } from "@/features/learner/components/learner-bulk-import/BulkChangeModal";
import { TBulkImportRow, buildAccountRowFromIssue } from "@/features/learner/components/learner-bulk-import/bulk-import-mock";
import {
  clearBulkImportRows,
  readBulkImportRows,
  readBulkImportSource,
  saveBulkImportRows,
  saveBulkImportSource,
  TBulkImportSource,
} from "@/features/learner/components/learner-bulk-import/bulk-import-storage";
import {
  buildBulkImportErrorMap,
  buildBulkImportPayload,
  countIncompleteBulkImportRows,
  countInvalidLastScoreRows,
  getFirstErrorRowId,
} from "@/features/learner/components/learner-bulk-import/bulk-import-payload";
import { bulkImportLearnersApi, issueAccountApi, useLearnerList } from "@/api/services/main/learner";
import { getIndustryPartTypes, useLearnerIndustryOptions } from "@/features/learner/hook/useLearnerIndustryOptions";
import { findOptionByLabel } from "@/features/learner/utils/master-matching";
import { useGetMunicipalityCode } from "@/hooks/useGetMunicipalityCode";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { useVisible } from "@/hooks/useVisible";
import { toast } from "@/utils/toast";
import { showError } from "@/utils/error";
import { API_SUCCESS_MESSAGE } from "@/constants/common";

// 重複・既存登録の検出表示は一時的に非表示にする (将来、検出APIと連携する際に true に戻す)。
const SHOW_VALIDATION_WARNINGS = false;

// 検証OFF時の安定した空エラー。毎回新しい {} を返すと defaultColumn(fieldErrors依存)が
// 編集の度に再生成され、テーブル全体が再処理されてしまうのを防ぐ。
const EMPTY_FIELD_ERRORS: Record<number, string[]> = {};

// 取り込みバリデーションの注意メッセージ (API連携前のモック)
const VALIDATION_MESSAGES = [
  "3行目、8行目の必須項目「氏名」が重複しています。",
  "4行目、5行目の必須項目「氏名」が重複しています。",
  "2行目の必須項目「氏名」が既に登録されています。",
];

export const BulkImportLearnerPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // 初期データの優先順位: モーダルからの新規選択 (location.state) > sessionStorage の作業中データ > 空(直接アクセス)。
  // 編集内容は sessionStorage に随時退避し、リロードしても失われないようにする。
  const incomingState = location.state as { rows?: TBulkImportRow[]; source?: TBulkImportSource } | null;
  const incomingRows = incomingState?.rows;
  const [rows, setRows] = useState<TBulkImportRow[]>(() => incomingRows ?? readBulkImportRows() ?? []);
  // 取り込み元 (visa 一括同期 / ファイル取り込み)。「戻る」で開き直すモーダルの判定に使う。
  const [source] = useState<TBulkImportSource>(() => incomingState?.source ?? readBulkImportSource());

  // 新規選択で来た場合のみ session を上書きし、location.state を消す。
  // これでリロード時は (未編集の) location.state ではなく編集済みの session から復元される。
  useEffect(() => {
    if (incomingRows) {
      saveBulkImportRows(incomingRows);
      saveBulkImportSource(source);
      navigate(AppRoutes.learnerBulkImport, { replace: true, state: null });
    }
  }, []);

  // 編集 / 一括変更 / 行削除のたびに作業中データを退避する (リロード復元用)。
  // 入力1文字ごとに50件を JSON.stringify すると重いため、約400msのデバウンスでまとめて書き込む。
  useEffect(() => {
    const timer = setTimeout(() => saveBulkImportRows(rows), 400);
    return () => clearTimeout(timer);
  }, [rows]);
  // チェックボックスで選択された行キー(getRowId と同じ文字列)。一括変更の対象に利用する。
  // 文字列キーで保持し突合も String(row.id) で行う: id が数値/数値文字列いずれで返っても確実に一致させるため。
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  // 一括変更の確定後にテーブルの選択(チェックボックス)を解除する指示。値を変えるたびにクリアさせる。
  const [clearSelectionNonce, setClearSelectionNonce] = useState(0);
  const selectedKeySet = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const hasSelection = selectedKeys.length > 0;
  // 既存登録(再取込)の検出: 同じ visa 従業員が既に learner として取り込み済みの行数 (BE prepare が判定)。
  // 0件超ならページ表示直後 (submit 不要) に画面上部へ警告バナーを表示する。情報提示のみで取り込みはブロックしない。
  const existingRegisteredCount = useMemo(() => rows.filter((row) => row.isAlreadyRegistered).length, [rows]);
  // バナーは ✕ で閉じられる。閉じた状態を保持する。
  const [existingBannerDismissed, setExistingBannerDismissed] = useState(false);
  // 一括変更モーダル (No.4)。選択行は同一受験分野に限定されるため、先頭選択行の industryId を共通値として渡す。
  const bulkModal = useVisible();
  const selectedIndustryId = useMemo(() => {
    const first = rows.find((row) => selectedKeySet.has(String(row.id)));
    return first?.industryId ?? null;
  }, [rows, selectedKeySet]);

  // 一括変更の確定: 選択中の行すべてに差分パッチを適用する (spec No.11)。
  // 手続き管理 / 学習アプリ の ON・OFF などをテーブルの該当行へ即時反映する。
  const handleBulkChange = useCallback(
    (patch: Partial<TBulkImportRow>) => {
      setRows((prev) => prev.map((row) => (selectedKeySet.has(String(row.id)) ? { ...row, ...patch } : row)));
      toast.success(`${selectedKeySet.size}件の外国人情報を一括変更しました。`);
      // 確定したら選択を解除する (対象行は変更済みのため、再度チェックしておく必要はない)。
      setClearSelectionNonce((n) => n + 1);
    },
    [selectedKeySet],
  );
  // アカウント発行対象は「学習アプリ」ON の行のみ (spec No.32)。1件もなければ発行ボタンを不可にする。
  const learningAppRows = rows.filter((row) => row.learningApp);
  const issueCount = learningAppRows.length;

  const handleChangeRow = useCallback((id: number, patch: Partial<TBulkImportRow>) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  }, []);

  const handleDeleteRow = useCallback((id: number) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  }, []);

  // 受験・学習対象のパートの既定値解決: 行ごとに分野(industryId)の全パートを ON にする (手動作成と同挙動)。
  // 分野マスタ(/industries)から partTypes/subjects を取り、SELECTABLE_PART_TYPES で絞り込む。
  const { getIndustryById, industryOptions } = useLearnerIndustryOptions();

  // 受験分野の名称→ID 解決。ファイル取り込みは FE で突合するため、マスタ取得前に解析した行や
  // 表記ゆれで ID が付かなかった行を、分野マスタが揃ったこの画面で再突合する
  // (セレクトは industryId でしか値を選べず、ID が無い行はチェック(保存対象)にもできないため)。
  // 対象は取り扱う分野が ON の分野のみ (OFF の分野へは取り込ませない)。
  // visa 同期は BE prepare の突合結果をそのまま尊重する (BE が付けなかった ID を FE で補わない)。
  useEffect(() => {
    if (source !== "file") return;
    if (industryOptions.length === 0) return;
    setRows((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        if (row.industryId != null || !row.industryName) return row;
        const matched = findOptionByLabel(industryOptions, row.industryName);
        if (!matched) return row;
        changed = true;
        return { ...row, industryId: Number(matched.value), industryName: matched.label };
      });
      return changed ? next : prev;
    });
  }, [industryOptions, source]);
  const resolvePartTypes = useCallback(
    (row: TBulkImportRow): string[] | null => {
      const parts = getIndustryPartTypes(getIndustryById(row.industryId));
      return parts.length > 0 ? parts : null;
    },
    [getIndustryById],
  );

  // 居住地の正規化: visa の都道府県/市区町村を study マスタの正規名へ寄せて保存する (手動作成フォームと同じ値)。
  // マスタに無い値は元の値を渡し、BE 側の fuzzy マッピングに委ねる。
  const { getProvinceName, getMunicipalityName } = useGetMunicipalityCode();
  const resolveResidence = useCallback(
    (row: TBulkImportRow) => ({
      prefecture: row.province ? getProvinceName(row.province) : null,
      city: row.municipality ? getMunicipalityName(row.municipality) : null,
    }),
    [getProvinceName, getMunicipalityName],
  );

  // 保存(一括取り込み)中はボタンをローディングにする。
  const [isSaving, setIsSaving] = useState(false);

  // 必須エラーの表示状態。検証後は行編集に追従してハイライトを更新する (修正すると自動で消える)。
  const [showErrors, setShowErrors] = useState(false);
  // ハイライト対象のスクロール先 (nonce で再クリック時も再スクロールさせる)。
  const [focusTarget, setFocusTarget] = useState<{ rowId: number; nonce: number } | null>(null);
  const fieldErrors = useMemo(() => (showErrors ? buildBulkImportErrorMap(rows) : EMPTY_FIELD_ERRORS), [showErrors, rows]);

  // 保存前の検証。必須未入力 or 前回点数の範囲超過 (0〜1000の整数) があれば赤枠表示 +
  // 先頭のエラー行へスクロールして false を返す。
  // 前回点数の不正はトーストを出さず赤枠のみで示す (必須未入力のみトーストを出す)。
  const validateBeforeSave = (): boolean => {
    const incompleteCount = countIncompleteBulkImportRows(rows);
    const invalidScoreCount = countInvalidLastScoreRows(rows);
    if (incompleteCount > 0 || invalidScoreCount > 0) {
      setShowErrors(true);
      const firstId = getFirstErrorRowId(rows);
      if (firstId != null) setFocusTarget({ rowId: firstId, nonce: Date.now() });
      if (incompleteCount > 0) toast.error(`${incompleteCount}件の必須項目が未入力です。修正後に取り込みを行ってください。`);
      return false;
    }
    setShowErrors(false);
    return true;
  };

  // 「戻る」で離脱するときは作業中データを破棄し、一覧で取り込みモーダルを開き直す。
  // 「戻る」は直前の手順へ戻す意味なので、取り込み元に応じたモーダル (visa 一括同期 / ファイル取り込み) を再オープンさせる。
  const leaveBack = () => {
    clearBulkImportRows();
    navigate(AppRoutes.learnerList, { state: source === "file" ? { openFileImport: true } : { openBulkSync: true } });
  };

  // 戻る: 作業中データがあれば「移動すると保存されない」旨を確認してから離脱する (spec: 未保存破棄)。
  const leaveConfirm = useVisible();
  const handleBack = () => {
    if (rows.length > 0) {
      leaveConfirm.open();
    } else {
      leaveBack();
    }
  };
  const confirmLeave = () => {
    leaveConfirm.close();
    leaveBack();
  };

  // 「追加だけして戻る」: learner を一括作成 (新規所属機関も名称から作成) して一覧へ戻る。
  // 必須項目に空欄があれば保存せず、修正を促すトーストを出す (spec No.2)。
  const handleImportOnly = async () => {
    if (!validateBeforeSave()) return;

    try {
      setIsSaving(true);
      await bulkImportLearnersApi(buildBulkImportPayload(rows, resolvePartTypes, resolveResidence));
      toast.success(API_SUCCESS_MESSAGE);
      clearBulkImportRows();
      navigate(AppRoutes.learnerList);
    } catch (e) {
      showError(e);
    } finally {
      setIsSaving(false);
    }
  };

  // アカウント発行の確認モーダル。
  const issueModal = useVisible();
  // 「有効アカウント数が XX 件になります」表示用に現在のアカウント数を取得する (spec No.32)。
  const { data: learnerListData } = useLearnerList({ page: 1, perPage: 1 });
  const currentAccountCount = learnerListData?.currentLearnerCount ?? 0;

  // 「XX件のアカウント一括発行」: 必須チェック → 確認モーダル。
  const handleIssueAccounts = () => {
    if (!validateBeforeSave()) return;
    issueModal.open();
  };

  // 確認後の本処理: learner を一括作成 (全行) → 学習アプリON の learner にアカウント発行 → 完了画面へ (spec No.32)。
  const runIssueAccounts = async () => {
    issueModal.close();
    try {
      setIsSaving(true);
      // 1. learner を一括作成。返り値 learners は入力(rows)順。
      const result = await bulkImportLearnersApi(buildBulkImportPayload(rows, resolvePartTypes, resolveResidence));
      // 安全弁: index で rows↔learners を突合するため、件数が食い違ったら誤ったlearnerへ発行しないよう中断する。
      if (result.learners.length !== rows.length) {
        toast.error("登録結果と件数が一致しませんでした。お手数ですが画面を再読み込みして再度お試しください。");
        return;
      }
      // 2. 学習アプリON の行に対応する learner にアカウント発行 (1件失敗しても他は続行)。
      const targets = rows
        .map((row, i) => ({ row, learnerId: result.learners[i]?.id }))
        .filter((t): t is { row: TBulkImportRow; learnerId: number } => t.row.learningApp && t.learnerId != null);
      const settled = await Promise.allSettled(
        targets.map((t) => issueAccountApi(t.learnerId).then((res) => buildAccountRowFromIssue(t.row, res.data))),
      );
      const issued = settled.flatMap((s) => (s.status === "fulfilled" ? [s.value] : []));
      const failedCount = settled.length - issued.length;

      clearBulkImportRows();
      if (failedCount > 0) toast.error(`${failedCount}件のアカウント発行に失敗しました。`);
      if (issued.length > 0) toast.success(`${issued.length}件のアカウントを発行しました。`);
      navigate(AppRoutes.learnerBulkImportComplete, { state: { accountRows: issued } });
    } catch (e) {
      showError(e);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    // 画面いっぱいに固定高 (100vh - AppHeader 56px - レイアウト余白 48px)。テーブルは内部スクロール
    <Stack gap={2} height="calc(100vh - 105px)">
      <Stack gap={0.5} flexShrink={0}>
        <Typography variant="head20Bold">外国人の一括登録</Typography>
        <Typography variant="body14Light" color={text.secondary}>
          次の外国人を取り込みます。問題がなければ登録またはアカウント一括発行へ進んでください。
        </Typography>
      </Stack>

      {/* 既存登録の警告バナー (情報提示のみ)。同じ visa 従業員が既に取り込み済みの場合に表示する。 */}
      {existingRegisteredCount > 0 && !existingBannerDismissed && (
        <Stack
          direction="row"
          alignItems="flex-start"
          gap={1}
          flexShrink={0}
          sx={{ p: 1.5, backgroundColor: orange[100], border: `1px solid ${orange[97]}`, borderRadius: rounded.sm }}
        >
          <IconWarningInfo width={20} height={20} style={{ flexShrink: 0 }} />
          <Typography variant="body14" color={text.primary} flex={1}>
            {/* 件数(太字)を1行目に、説明文を2行目に分ける (design 準拠) */}
            <Box component="span" sx={{ fontWeight: 700, display: "block" }}>
              {existingRegisteredCount}件の既存登録
            </Box>
            が検出されました。修正が必要な場合は外国人選択からやり直してください。
          </Typography>
          <IconBox onClick={() => setExistingBannerDismissed(true)} sx={{ p: 0 }}>
            <IconClose color={black[80]} fontSize={20} />
          </IconBox>
        </Stack>
      )}

      <Stack flex={1} minHeight={0} gap={1.5} sx={{ border: `1px solid ${divider.high}`, borderRadius: rounded.sm, p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexShrink={0}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="body14Semi" color={text.primary}>
              取り込み対象： {rows.length} 件
            </Typography>
            {hasSelection && (
              <Typography variant="cap12Bold" color={orange[70]}>
                {selectedKeys.length} 件選択中
              </Typography>
            )}
          </Stack>
          <Button variant="outline" size="sm" disabled={!hasSelection} onClick={bulkModal.open}>
            <Typography variant="body14Semi">選択した外国人情報の一括変更</Typography>
          </Button>
        </Stack>

        {/* StyledTable → Paper → TableContainer まで全て高さ100%にして本体を下端まで広げる。
            これで行が少なくても余白が白く埋まり、横スクロールバーがテーブル下端に出る */}
        <Box
          flex={1}
          minHeight={0}
          sx={{
            "& > div": { height: "100%", backgroundColor: background.white },
            "& .MuiPaper-root": { height: "100%", backgroundColor: background.white },
          }}
        >
          <BulkImportLearnerTable
            rows={rows}
            onChangeRow={handleChangeRow}
            onDeleteRow={handleDeleteRow}
            onSelectionChange={setSelectedKeys}
            fieldErrors={fieldErrors}
            focusTarget={focusTarget}
            clearSelectionNonce={clearSelectionNonce}
          />
        </Box>

        {SHOW_VALIDATION_WARNINGS && VALIDATION_MESSAGES.length > 0 && (
          <Stack
            flexShrink={0}
            gap={0.5}
            sx={{ p: 1.5, backgroundColor: orange[100], border: `1px solid ${orange[97]}`, borderRadius: rounded.sm }}
          >
            {VALIDATION_MESSAGES.map((message) => (
              <Typography key={message} variant="cap12" color={orange[70]}>
                {message}
              </Typography>
            ))}
          </Stack>
        )}
      </Stack>

      <FooterContainer>
        <Stack direction="row" alignItems="center" justifyContent="space-between" height="100%">
          <Button variant="ghost" size="md" onClick={handleBack}>
            <Typography variant="body14Semi">戻る</Typography>
          </Button>
          <Stack direction="row" gap={2}>
            <Button variant="outline" size="md" onClick={handleImportOnly} isLoading={isSaving} disabled={isSaving || rows.length === 0}>
              <Typography variant="body14Semi">追加だけして戻る</Typography>
            </Button>
            <Button size="md" startIcon={<IconSave />} onClick={handleIssueAccounts} isLoading={isSaving} disabled={issueCount === 0 || isSaving}>
              <Typography variant="body14Semi">上記全てのアカウントを一括発行</Typography>
            </Button>
          </Stack>
        </Stack>
      </FooterContainer>

      <BulkChangeModal
        open={bulkModal.visible}
        targetCount={selectedKeys.length}
        industryId={selectedIndustryId}
        onClose={bulkModal.close}
        onApply={handleBulkChange}
      />

      <ConfirmSaveModal open={issueModal.visible} onClose={issueModal.close} onOk={runIssueAccounts} title="アカウント一括発行">
        <Typography variant="body14">
          新たに{issueCount}件のアカウントを発行します。有効アカウント数が{currentAccountCount + issueCount}件になりますがよろしいですか？
        </Typography>
      </ConfirmSaveModal>

      <ConfirmSaveModal open={leaveConfirm.visible} onClose={leaveConfirm.close} onOk={confirmLeave} title="ページの移動">
        <Typography variant="body14">このページを移動すると、入力内容は保存されません。移動してもよろしいですか？</Typography>
      </ConfirmSaveModal>
    </Stack>
  );
};
